import { parseValidationAddressComponents } from "@/DataPages/helpers/crm/utils";
import { useQuery } from "@tanstack/react-query";

// Constants
import { GOOGLE_API } from "@/Constants/api";

const API_KEY = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY;
const FIVE_MINUTES = 300000; // 5 minutes in milliseconds (1000 * 60 * 5)

function processValidationResult(validationResult: IAddressValidationResponse["result"]): boolean {
	const {
		verdict,
		address: { addressComponents },
	} = validationResult;
	const isComplete = Boolean(verdict?.addressComplete);
	const isConfirmed = addressComponents.every((component) =>
		["UNCONFIRMED_BUT_PLAUSIBLE", "CONFIRMED"].includes(component.confirmationLevel)
	);
	const hasPreciseGranularity = ["PREMISE", "SUB_PREMISE"].includes(verdict.validationGranularity);
	return isComplete || (isConfirmed && hasPreciseGranularity);
}

async function fetchAddressValidation(address: string, regionCode?: string): Promise<GoogleAddressValidation> {
	const params = new URLSearchParams({
		key: API_KEY,
	});

	const response = await fetch(`${GOOGLE_API.validateAddress}?${params.toString()}`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			address: {
				addressLines: [address],
				regionCode,
			},
		}),
	});

	if (!response.ok) {
		const { error } = await response.json();
		throw new Error(`${error?.message ?? "Unknown error"}`);
	}

	const { result: validationResult } = (await response.json()) as IAddressValidationResponse;

	const { verdict, address: recommendedAddress, metadata, geocode } = validationResult ?? {};
	const { formattedAddress, postalAddress, addressComponents } = recommendedAddress ?? {};

	return {
		verdict,
		geocode,
		isValid: processValidationResult(validationResult),
		recommendedAddress: {
			postalAddress,
			addressComponents,
			formatted: formattedAddress,
			parsed: parseValidationAddressComponents(addressComponents, postalAddress, metadata),
		},
	};
}

export const useAddressValidation = (params: { address: string; countryCode?: string }) => {
	const { address, countryCode } = params;

	return useQuery({
		queryKey: ["validate-address", address],
		queryFn: () => fetchAddressValidation(address, countryCode),
		enabled: !!address,
		staleTime: FIVE_MINUTES,
	});
};
